import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from './AuthContext';

const WebSocketContext = createContext(null);

// Build WebSocket URL from current page origin so it works on any host/port.
// REACT_APP_WS_URL can override this for custom deployments.
const WS_URL = process.env.REACT_APP_WS_URL || (() => {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}/ws`;
})();

export const WebSocketProvider = ({ children }) => {
  const [connected, setConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const ws = useRef(null);
  const reconnectTimeout = useRef(null);
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    connect();
    
    return () => {
      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
      }
      if (ws.current) {
        // Detach first: onclose would otherwise schedule a reconnect for a
        // provider that no longer exists (a second socket per remount).
        ws.current.onclose = null;
        ws.current.close();
      }
    };
  }, []);

  // A login after the socket opened (or a token refresh) must still authenticate
  // it; onopen only covers a token that already existed at connect time.
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (isAuthenticated && token) {
      send({ type: 'authenticate', token });
    }
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  const connect = () => {
    if (ws.current?.readyState === WebSocket.OPEN) return;

    ws.current = new WebSocket(WS_URL);

    ws.current.onopen = () => {
      console.log('WebSocket connected');
      setConnected(true);
      
      // Authenticate if we have a token
      const token = localStorage.getItem('access_token');
      if (token) {
        send({ type: 'authenticate', token });
      }
    };

    ws.current.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        // Ignore heartbeats, they just keep the proxy alive
        if (data.type === 'heartbeat' || data.type === 'ping') {
          return;
        }

        setLastMessage(data);
      } catch (err) {
        console.error('WS parse error:', err);
      }
    };

    ws.current.onclose = () => {
      console.log('WebSocket disconnected');
      setConnected(false);
      // Reconnect after 3 seconds
      reconnectTimeout.current = setTimeout(connect, 3000);
    };

    ws.current.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
  };

  const send = (data) => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify(data));
    }
  };

  const watchArticle = (articleId) => {
    send({ type: 'watch_article', article_id: articleId });
  };

  const unwatchArticle = (articleId) => {
    send({ type: 'unwatch_article', article_id: articleId });
  };

  const value = {
    connected,
    lastMessage,
    send,
    watchArticle,
    unwatchArticle
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};
